import { icons } from '../../icons';
import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { TripWizardConfig } from './types';

function Header({ config }: { config: TripWizardConfig }) {
  const { t } = config;
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-2 text-sm">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-900 text-xs font-bold text-white">
          K
        </span>
        <span className="font-semibold text-gray-900">KOOB</span>
        <span className="text-gray-300">/</span>
        <span className="text-gray-500">{t('header')}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-xs uppercase tracking-widest text-gray-400">
          {t('title')}
        </span>
        {config.onExit && (
          <button
            type="button"
            aria-label={t('close')}
            className="border-none bg-transparent text-lg text-gray-400 hover:text-gray-800"
            onClick={() => config.onExit?.()}
          >
            <i className={icons.close} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The modal chrome shared by both wizard modes — the step-by-step create flow
 * and the single-screen edit form. Portalled: the hosts mount the wizard deep
 * inside the navbar or a page, whose stacking context would otherwise let the
 * sidebar paint over it.
 */
export function WizardOverlay({
  config,
  children,
}: {
  config: TripWizardConfig;
  children: ReactNode;
}) {
  // Escape leaves — unless an open dropdown wants it first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = document.activeElement as HTMLElement | null;
      const role = el?.getAttribute?.('role');
      const inMenu = role === 'combobox' || role === 'listbox';
      if (inMenu && el?.getAttribute('aria-expanded') === 'true') return;
      config.onExit?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [config]);

  // On both wrappers: the outer one owns the 1rem gutter, the inner one the
  // space above/below and beside the panel.
  const closeOnBackdrop = (e: MouseEvent) => {
    if (e.target === e.currentTarget) config.onExit?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-modal overflow-y-auto bg-gray-500 bg-opacity-75 p-4 backdrop-blur-sm"
      onClick={closeOnBackdrop}
    >
      {/* The panel carries its own width so it stays put from one step to the
          next — a shrink-to-fit one would resize with the content. */}
      <div
        className="flex min-h-full w-full flex-col items-center justify-center"
        onClick={closeOnBackdrop}
      >
        <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <Header config={config} />
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
